import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import MuiDialog from '@mui/material/Dialog';
import { useDataProps, useAriaProps } from '@famly/mf_ts-utils_data-aria-props';
import { closeModalOnEscapeKeyDown } from './close-modal-on-escape-key-down';
import { triggerModalPrimaryAction } from './trigger-modal-primary-action';
// width
const compactWidth = {
    base: '95%',
    mobileLandscape: '450px',
};
const regularWidth = {
    base: '100%',
    mobileLandscape: '600px',
};
// height
const compactHeight = {
    base: '400px',
    mobileLandscape: 'auto',
};
const regularHeight = {
    base: '100%',
    mobileLandscape: 'auto',
};
// borderRadius
const compactBorderRadius = {
    base: '16px',
};
const regularBorderRadius = {
    mobileLandscape: '16px',
};
// padding
const compactPadding = {
    base: '16px',
};
// `regular` modals fill the whole viewport on small screens (see `regularHeight`/`regularMargin`), so their
// top padding must clear the iOS safe area — otherwise the header sits under the notch / Dynamic Island.
// Order below is CSS shorthand (top, right, bottom, left); only the top side needs the safe-area inset.
// `env(safe-area-inset-top)` is only non-zero where the app shell's viewport meta tag declares
// `viewport-fit=cover` (the iOS app shell); it resolves to 0 elsewhere, so this falls back to a flat 24px.
const regularPadding = {
    base: 'max(24px, env(safe-area-inset-top)) 24px 24px 24px',
    mobileLandscape: '24px',
};
// margin
const compactMargin = {
    base: '16px',
    mobileLandscape: 0,
};
const regularMargin = {
    base: 0,
    mobileLandscape: undefined,
};
/**
 * Base component for implementing modern famly dialogs.
 *
 * Can take an arbitrary element as children, but it is recommended to use the other dialogs elements defined
 * in this same project
 *
 * Pressing `Escape` closes the modal and pressing `Cmd`/`Ctrl` + `Enter` triggers its primary action.
 *
 * @param open tells if the dialog should be shown or not
 * @param size tells if we should render a compact or regular sized modal
 * @param onClose called when the user dismisses the modal with the `Escape` key
 */
export const Modal = ({ open, size, onClose, zIndex, children, ...rest }) => {
    const dataAttributes = useDataProps(rest);
    const ariaAttributes = useAriaProps(rest);
    return (_jsx(MuiDialog, { open: open, onClose: closeModalOnEscapeKeyDown(onClose), onKeyDown: triggerModalPrimaryAction, ...dataAttributes, ...ariaAttributes, sx: () => (zIndex !== undefined ? { zIndex } : {}), PaperProps: {
            sx: theme => {
                var _a;
                return ({
                    width: size === 'compact' ? compactWidth : regularWidth,
                    height: size === 'compact' ? compactHeight : regularHeight,
                    margin: size === 'compact' ? compactMargin : regularMargin,
                    padding: size === 'compact' ? compactPadding : regularPadding,
                    borderRadius: size === 'compact' ? compactBorderRadius : regularBorderRadius,
                    boxShadow: (_a = theme.modernFamlyTheme) === null || _a === void 0 ? void 0 : _a.elevation[4],
                    maxHeight: {
                        mobileLandscape: '90svh',
                    },
                });
            },
        }, children: children }));
};
